<template>
    <div
        id="searchResultLoop"
        class="af-search-result-items"
    >
        <SearchNoResults v-if="!jobs.length" />
        <template v-else>
            <div
                v-for="job in jobs"
                :key="job.uuid + '_singlejob'"
                class="af-search-result-item afu-mb-4 afu-border afu-border-solid afu-rounded-secondary afu-border-neutral afu-bg-white hover:afu-shadow-card afu-transition afu-duration-300 hover:afu-border-solid hover:afu-border-transparent afu-group afu-relative"
            >
                <div class="af-search-result-item-header afu-p-5 md:afu-p-6 afu-flex afu-flex-col md:afu-flex-row afu-content-between">
                    <a
                        :href="getJobViewLink(job.URL)"
                        class="afu-absolute afu-w-full afu-h-full afu-z-0 afu-left-0 afu-top-0"
                    />
                    <div
                        v-if="showCompanyLogo && job.company_square_logo_url"
                        class="af-search-result-item-logo af-search-result-item-logo afu-w-40 afu-text-left afu-flex-col"
                    >
                        <img
                            v-if="showCompanyLogo && job.company_square_logo_url"
                            class="afu-max-w-xs afu-max-h-xxs afu-self-center"
                            :src="job.company_square_logo_url"
                            :alt="job.company_name"
                            :title="job.company_name"
                        >
                    </div>
                    <div class="af-job-content md:afu-w-4/5">
                        <div class="af-search-result-job-date af-text--base afu-text-neutral-dark md:afu-hidden afu-mb-2">
                            {{ $t('job_view.posted_on') }} {{ job.activates_at | dateFormat('MMM YYYY') }}
                        </div>
                        <div :class="{'af-job-title af-text--4xl afu-flex afu-flex-col': true}">
                            <a
                                :href="getJobViewLink(job.URL)"
                                class="afu-text-black afu-font-semibold afu-no-underline afu-flex afu-items-center afu-gap-2"
                            >
                                <span v-html="job.job_title" />
                                <Badge v-if="getJobBadge(job.activates_at)"
                                       size="xs"
                                       class="afu-font-sans afu-font-thin">{{ getJobBadge(job.activates_at) }}
                                </Badge>
                            </a>
                        </div>
                        <div class="af-job-features afu-grid afu-grid-cols-1 md:afu-grid-cols-2 afu-gap-4 af-text--sm afu-text-black afu-mt-3 md:afu-mt-4">
                            <div class="af-job-features-left">
                                <ul class="afu-list-none afu-m-0 afu-p-0 afu-text-secondary afu-font-bold">
                                    <li
                                        class="af-job-feature-location"
                                    >
                                        {{ job.location_label }}
                                    </li>
                                    <li
                                        v-if="!parseInt(job.pay_is_hidden)"
                                        class="af-job-feature-salary afu-my-1"
                                    >
                                        <span v-html="getMinMaxLabel(job)" />
                                    </li>
                                    <li
                                        class="af-job-feature-salary af-pay-description afu-my-1"
                                    >
                                        {{ job.pay_description }}
                                    </li>
                                    <li
                                        v-if="job.custom_detail_1 && showCustomAttribute(1)"
                                        :class="['af-job-feature-type', 'afu-my-1', `af-label-${job.custom_detail_1[0].code}`]"
                                    >
                                        {{ job.custom_detail_1[0].value }}
                                    </li>
                                    <li
                                        v-if="job.custom_detail_2 && showCustomAttribute(2)"
                                        :class="['af-job-feature-type', 'afu-my-1', `af-label-${job.custom_detail_2[0].code}`]"
                                    >
                                        {{ job.custom_detail_2[0].value }}
                                    </li>
                                    <li
                                        v-if="job.custom_detail_3 && showCustomAttribute(3)"
                                        :class="['af-job-feature-type', 'afu-my-1', `af-label-${job.custom_detail_3[0].code}`]"
                                    >
                                        {{ job.custom_detail_3[0].value }}
                                    </li>
                                    <li
                                        v-if="job.custom_detail_4 && showCustomAttribute(4)"
                                        :class="['af-job-feature-type', 'afu-my-1', `af-label-${job.custom_detail_4[0].code}`]"
                                    >
                                        {{ job.custom_detail_4[0].value }}
                                    </li>
                                </ul>
                            </div>
                            <div class="af-job-features-right md:afu-px-4">
                                <template v-if="job.job_bullets && job.job_bullets.length">
                                    <ul
                                        class="afu-list-disc afu-m-0 afu-p-0 afu-mb-4 afu-ml-4"
                                    >
                                        <li
                                            v-for="(job_bullet, index) in job.job_bullets"
                                            :key="`${job.uuid}_${index}_bullet`"
                                            class="afu-mb-1"
                                        >
                                            {{ job_bullet }}
                                        </li>
                                    </ul>
                                </template>
                                <template v-if="job.short_description && job.short_description.length">
                                    <div
                                        class="short_description afu-m-0 afu-p-0"
                                        v-html="job.short_description.length > 150 ? job.short_description.substring(0, 150) + ' ...' : job.short_description"
                                    />
                                </template>
                                <template v-else>
                                    <div
                                        class="job_description afu-m-0 afu-p-0"
                                        v-html="job.job_description.length > 150 ? job.job_description.substring(0, 150) + ' ...' : job.job_description"
                                    />
                                </template>
                            </div>
                        </div>
                    </div>
                    <MqLayout
                        mq="tsm+"
                        class="af-search-result-actions afu-ml-auto afu-flex afu-flex-col afu-w-40"
                    >
                        <div class="af-search-result-job-date afu-text-right af-text--sm afu-text-neutral-dark">
                            {{ $t('job_view.posted_on') }} {{ job.activates_at | dateFormat('MMM YYYY') }}
                        </div>
                        <div class="afu-mt-4 afu-text-right">
                            <SaveJob
                                :job-uuid="job.uuid"
                                size="sm"
                                class="afu-mb-2 afu-relative"
                            />
                            <QuickApplyJob
                                :job="job"
                                :job-uuid="job.uuid"
                                :is-offsite-apply="job.is_offsite_apply"
                                class="afu-relative"
                            >
                                {{ $t('label.quick_apply') }}
                            </QuickApplyJob>
                        </div>
                    </MqLayout>
                    <MqLayout :mq="['sm']">
                        <div class="af-search-result-actions af-search-result-actions-mobile afu-flex afu-gap-2 afu-mt-4 md:afu-my-4">
                            <SaveJob
                                :job-uuid="job.uuid"
                                class="afu-relative"
                            />
                            <EmailJob
                                :job-title="job.job_title"
                                :job-url="getJobViewLink(job.URL)"
                            />
                        </div>
                    </MqLayout>
                </div>
                <div class="af-search-result-item-footer afu-bg-neutral-light afu-px-6 afu-py-3 afu-flex afu-justify-between afu-items-center md:afu-mt-4 group-hover:afu-border group-hover:afu-border-solid group-hover:afu-border-t-0 group-hover:afu-border-transparent">
                    <div class="af-search-result-job-classifications afu-flex afu-flex-col afu-gap-1">
                        <div
                            v-for="(classification, index2) in job.classification_relationship"
                            :key="index2"
                        >
                            <div v-if="classification.subclassifications.length > 0">
                                <div
                                    v-for="(subclassification, index) in classification.subclassifications"
                                    class="af-job-classification afu-text-secondary af-text--sm afu-block afu-font-semibold"
                                    :key="`${index}_${index2}`"
                                >
                                    <a
                                        class="afu-text-secondary afu-no-underline afu-relative"
                                        :href="getClassificationLink(classification)"
                                    >{{ classification.value }}</a>
                                    <FontAwesomeIcon
                                        :icon="['fal', 'angle-right']"
                                        class="afu-mx-1 afu-w-2 afu-text-secondary"
                                    />
                                    <a
                                        class="afu-text-secondary afu-no-underline afu-relative"
                                        :href="getSubClassificationLink(classification, subclassification)"
                                    >{{ subclassification.value }}</a>
                                </div>
                            </div>
                            <div v-else>
                                <div
                                    class="af-job-classification afu-text-secondary af-text--sm afu-block afu-font-semibold"
                                >
                                    <a
                                        class="afu-text-secondary afu-no-underline afu-relative"
                                        :href="getClassificationLink(classification)"
                                    >{{ classification.value }}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import Filters from '@/components/Utils/Filters';
    import { payMinMaxLabel, getSearchBaseUrl, getJobViewBaseUrl, slugify } from '@/utils/helpers';
    import SearchNoResults from './SearchNoResults';
    import SaveJob from '@/apps/jobview/001/components/Actions/SaveJob';
    import EmailJob from '@/apps/jobview/001/components/Actions/EmailJob';
    import QuickApplyJob from "@/apps/jobview/001/components/Actions/QuickApplyJob.vue";
    import Badge from '@/components/Badge.vue';
    import moment from 'moment';

    export default {
        name: 'SearchResultLoop',

        components: {
            QuickApplyJob,
            Badge,
            SearchNoResults,
            SaveJob,
            EmailJob,
        },

        mixins: [Filters],

        props: {
            jobs: {
                type: Array,
                default: () => [],
            },
        },

        data() {
            return { 
                lastHost: null, 
                bulletpoints: window?.jobsResultsConfig?.bulletpoints || 'hide',
                shortdescription: window?.jobsResultsConfig?.shortdescription || 'hide',
                charlimit: window?.jobsResultsConfig?.charlimit || 150,
            };
        },

        computed: {
            showCompanyLogo() {
                // return true;
                return window?.afConfig?.show_company_logo === 'Yes';
            },
        },

        methods: {
            getJobBadge(activatesAt) {
                const postedDate = moment.utc(activatesAt);
                const hoursAgo = moment.utc().diff(postedDate, 'hours');

                if (hoursAgo <= 24) {
                    return this.$t('label.just_posted');
                }

                const daysAgo = moment.utc().diff(postedDate, 'days');
                if (daysAgo <= 3) {
                    return this.$t('label.new');
                }

                return '';
            },

            getClassificationLink(classification) {
                const classificationLink = `${getSearchBaseUrl()}/cla_${classification.slug || slugify(classification.value)}/`;
                return classificationLink;
            },

            getSubClassificationLink(classification, subClassifiacation) {
                const subClassificationLink = `${getSearchBaseUrl()}/cla_${classification.slug || slugify(classification.value)}_scla_${subClassifiacation.slug || slugify(subClassifiacation.value) }/`;
                return subClassificationLink;
            },

            getJobViewLink(job_uuid) {
                return `${getJobViewBaseUrl()}/${job_uuid}/`;
            },

            getMinMaxLabel(job) {
                return payMinMaxLabel(job, this.$t('global.currency_symbol'));
            },

            showCustomAttribute(index) {
                return window?.afConfig[`applyflow_custom_${index}_show`] === 'Yes';
            },
            sanitizeCharLimitValue(value) {
                if(/^\d+$/.test(value)) {
                    return parseInt(value);
                }
                else {
                    return 150;
                }
            },
            displayOption(job) {
                let displayOption = '';
                if(this.bulletpoints === 'hide' && this.shortdescription === 'hide') {
                    if(job.short_description && job.short_description.length)
                        displayOption = 'showShortDesc';
                    else if(job.job_bullets && job.job_bullets.length)
                        displayOption = 'showBulletPoints';
                    else 
                        displayOption = 'showFullDesc';
                }
                else if(this.bulletpoints === 'show' && this.shortdescription === 'show') {
                    if(!job.job_bullets?.length || !job.short_description?.length) {
                        if(job.short_description && job.short_description.length)
                            displayOption = 'showShortDesc';
                        else if(job.job_bullets && job.job_bullets.length)
                            displayOption = 'showBulletPoints';
                        else 
                            displayOption = 'showFullDesc';
                    }
                    else 
                        displayOption = 'showAll';
                }
                else if(this.bulletpoints === 'show') {
                    if(job.job_bullets && job.job_bullets.length)
                        displayOption = 'showBulletPoints';
                    else if(job.short_description && job.short_description.length)
                        displayOption = 'showShortDesc';
                    else 
                        displayOption = 'showFullDesc';
                }
                else {
                    if(job.short_description && job.short_description.length)
                        displayOption = 'showShortDesc';
                    else if(job.job_bullets && job.job_bullets.length)
                        displayOption = 'showBulletPoints';
                    else 
                        displayOption = 'showFullDesc';
                }
                return displayOption;
            },

        },
    };
</script>
